import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Mail, Phone, MapPin, Send, CheckCircle, AlertCircle } from 'lucide-react';
import toast from '../utils/toast';
import FormInput from '../components/FormInput';
import FormTextArea from '../components/FormTextArea';
import FormSelect from '../components/FormSelect';
import { validateForm, commonValidationRules } from '../utils/formValidation';

const ContactForm = () => {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: '',
        inquiryType: 'general',
        honeypot: '' // Bot detection field
    });
    const [loading, setLoading] = useState(false);
    const [submitStatus, setSubmitStatus] = useState('');
    const [errors, setErrors] = useState({});
    const [formStartTime] = useState(Date.now()); // Track form load time for bot detection

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));

        // Clear error for this field
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: null }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Check honeypot field (should be empty)
        if (formData.honeypot) {
            console.warn('Bot detected - honeypot filled');
            return;
        }

        // Validate form
        const newErrors = validateForm(formData, commonValidationRules.contactForm);
        setErrors(newErrors);

        if (Object.keys(newErrors).length > 0) {
            return;
        }

        setLoading(true);
        setSubmitStatus('');

        try {
            // Prepare submission data
            const submissionData = {
                name: formData.name,
                email: formData.email,
                phone: formData.phone,
                subject: formData.subject,
                message: formData.message,
                inquiryType: formData.inquiryType,
                submissionTime: formStartTime // For bot detection
            };

            // Call API endpoint
            const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';
            const response = await fetch(`${API_URL}/api/contact`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(submissionData)
            });

            const data = await response.json();

            if (response.ok && data.success) {
                setSubmitStatus('success');
                toast.success(data.message || 'Message sent successfully! We\'ll get back to you soon.');

                // Reset form
                setFormData({
                    name: '',
                    email: '',
                    phone: '',
                    subject: '',
                    message: '',
                    inquiryType: 'general',
                    honeypot: ''
                });
            } else {
                throw new Error(data.error || 'Failed to send message');
            }
        } catch (error) {
            setSubmitStatus('error');
            console.error('Contact form error:', error);

            // Show user-friendly error message
            const errorMessage = error.message || 'Failed to send message. Please try again or contact us directly.';
            toast.error(errorMessage);

            // Show fallback contact info if available
            if (error.fallback) {
                setTimeout(() => {
                    toast.info(`You can also reach us at: ${error.fallback.email} or ${error.fallback.phone}`);
                }, 2000);
            }
        } finally {
            setLoading(false);
        }
    };

    const contactInfo = [
        {
            icon: Phone,
            title: "Phone Support",
            details: ["+220 404 0966", "+220 704 0760"],
            description: "Call us for immediate assistance"
        },
        {
            icon: Mail,
            title: "Email Support",
            details: ["info@amhas.org"],
            description: "Send us an email anytime"
        },
        {
            icon: MapPin,
            title: "Office Locations",
            details: ["Banjul, The Gambia", "Yaoundé, Cameroon"],
            description: "Visit our regional offices"
        }
    ];

    const inquiryTypes = [
        { value: 'general', label: 'General Inquiry' },
        { value: 'enrollment', label: 'Student Enrollment' },
        { value: 'school-enrollment', label: 'School Enrollment' },
        { value: 'technical-support', label: 'Technical Support' },
        { value: 'billing', label: 'Billing & Payments' },
        { value: 'complaint', label: 'Complaint' },
        { value: 'other', label: 'Other' }
    ];

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Hero Section */}
            <section className="relative bg-gradient-to-br from-primary-600 via-primary-700 to-primary-800 text-white py-20">
                <div className="container-custom">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                        className="text-center max-w-4xl mx-auto"
                    >
                        <h1 className="text-5xl md:text-6xl font-bold mb-6">Contact Us</h1>
                        <p className="text-xl md:text-2xl text-primary-100 mb-8">
                            We're here to help! Get in touch with our team for any questions or support.
                        </p>
                    </motion.div>
                </div>
            </section>

            {/* Contact Form Section */}
            <section className="section-padding bg-white">
                <div className="container-custom">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
                        {/* Contact Form */}
                        <motion.div
                            initial={{ opacity: 0, x: -30 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.8 }}
                            viewport={{ once: true }}
                        >
                            <div className="bg-white rounded-2xl shadow-xl p-8">
                                <h2 className="text-3xl font-bold text-gray-900 mb-6">Send us a Message</h2>

                                {submitStatus === 'success' && (
                                    <motion.div
                                        initial={{ opacity: 0, y: 20 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center space-x-3"
                                    >
                                        <CheckCircle className="w-5 h-5 text-green-600" />
                                        <span className="text-green-800">Message sent successfully! We'll get back to you soon.</span>
                                    </motion.div>
                                )}

                                {submitStatus === 'error' && (
                                    <motion.div
                                        initial={{ opacity: 0, y: 20 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center space-x-3"
                                    >
                                        <AlertCircle className="w-5 h-5 text-red-600" />
                                        <span className="text-red-800">Failed to send message. Please try again.</span>
                                    </motion.div>
                                )}

                                <form onSubmit={handleSubmit} className="space-y-6">
                                    {/* Honeypot field for bot detection - hidden from users */}
                                    <input
                                        type="text"
                                        name="honeypot"
                                        value={formData.honeypot}
                                        onChange={handleInputChange}
                                        style={{ display: 'none' }}
                                        tabIndex="-1"
                                        autoComplete="off"
                                        aria-hidden="true"
                                    />

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        <FormInput
                                            label="Full Name"
                                            name="name"
                                            type="text"
                                            value={formData.name}
                                            onChange={handleInputChange}
                                            placeholder="Your full name"
                                            required={true}
                                            error={errors.name}
                                        />
                                        <FormInput
                                            label="Email Address"
                                            name="email"
                                            type="email"
                                            value={formData.email}
                                            onChange={handleInputChange}
                                            placeholder="your.email@example.com"
                                            required={true}
                                            error={errors.email}
                                        />
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        <FormInput
                                            label="Phone Number"
                                            name="phone"
                                            type="tel"
                                            value={formData.phone}
                                            onChange={handleInputChange}
                                            placeholder="+220 XXX XXX XXX"
                                            required={false}
                                            error={errors.phone}
                                        />
                                        <FormSelect
                                            label="Inquiry Type"
                                            name="inquiryType"
                                            value={formData.inquiryType}
                                            onChange={handleInputChange}
                                            required={true}
                                            error={errors.inquiryType}
                                            options={inquiryTypes}
                                            placeholder="Select inquiry type"
                                        />
                                    </div>

                                    <FormInput
                                        label="Subject"
                                        name="subject"
                                        type="text"
                                        value={formData.subject}
                                        onChange={handleInputChange}
                                        placeholder="Brief subject of your inquiry"
                                        required={true}
                                        error={errors.subject}
                                    />

                                    <FormTextArea
                                        label="Message"
                                        name="message"
                                        value={formData.message}
                                        onChange={handleInputChange}
                                        placeholder="Please provide details about your inquiry..."
                                        required={true}
                                        error={errors.message}
                                        rows={6}
                                    />

                                    <button
                                        type="submit"
                                        disabled={loading}
                                        className="w-full bg-primary-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                                    >
                                        {loading ? (
                                            <>
                                                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                                <span>Sending...</span>
                                            </>
                                        ) : (
                                            <>
                                                <Send size={20} />
                                                <span>Send Message</span>
                                            </>
                                        )}
                                    </button>
                                </form>
                            </div>
                        </motion.div>

                        {/* Contact Information */}
                        <motion.div
                            initial={{ opacity: 0, x: 30 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.8 }}
                            viewport={{ once: true }}
                            className="space-y-8"
                        >
                            <div>
                                <h2 className="text-3xl font-bold text-gray-900 mb-6">Get in Touch</h2>
                                <p className="text-gray-600 text-lg mb-8">
                                    Have questions about AMHAS? Need help with enrollment? Want to provider with us?
                                    We're here to help you every step of the way.
                                </p>
                            </div>

                            <div className="space-y-6">
                                {contactInfo.map((info, index) => (
                                    <motion.div
                                        key={info.title}
                                        initial={{ opacity: 0, y: 20 }}
                                        whileInView={{ opacity: 1, y: 0 }}
                                        transition={{ duration: 0.6, delay: index * 0.1 }}
                                        viewport={{ once: true }}
                                        className="bg-gray-50 rounded-xl p-6"
                                    >
                                        <div className="flex items-start space-x-4">
                                            <div className="bg-primary-100 rounded-lg p-3">
                                                <info.icon size={24} className="text-primary-600" />
                                            </div>
                                            <div>
                                                <h3 className="text-xl font-semibold text-gray-900 mb-2">{info.title}</h3>
                                                <p className="text-gray-600 mb-3">{info.description}</p>
                                                <div className="space-y-1">
                                                    {info.details.map((detail, idx) => (
                                                        <div key={idx} className="text-primary-600 font-medium">
                                                            {detail}
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                    </motion.div>
                                ))}
                            </div>

                            <div className="bg-primary-50 rounded-xl p-6">
                                <h3 className="text-xl font-semibold text-gray-900 mb-3">Response Time</h3>
                                <p className="text-gray-600">
                                    We typically respond to inquiries within 24 hours during business days.
                                    For urgent matters, please call us directly.
                                </p>
                            </div>
                        </motion.div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default ContactForm;
